/**
 * Add Section Dialog Component
 * Modal for adding a new section to a BOQ — Studio theme
 */

import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { FieldInput, FloatPanel, StudioButton } from "@/features/studio/theme";
import { addBOQSection } from "../../services/boq/boq-api.service";

interface AddSectionDialogProps {
	boqId: string;
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess: () => void;
}

// ── Shared styled label ────────────────────────────────────────────────────────
function FieldLabel({
	children,
	htmlFor,
}: {
	children: React.ReactNode;
	htmlFor?: string;
}) {
	return (
		<label
			htmlFor={htmlFor}
			style={{
				display: "block",
				fontSize: 11,
				fontFamily: "var(--font-mono)",
				fontWeight: 600,
				letterSpacing: "0.09em",
				textTransform: "uppercase",
				color: "var(--color-dashboard-muted)",
				marginBottom: 6,
			}}
		>
			{children}
		</label>
	);
}

// ── Shared styled textarea ─────────────────────────────────────────────────────
function FieldTextarea({
	id,
	placeholder,
	value,
	onChange,
	rows = 3,
}: {
	id?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	rows?: number;
}) {
	return (
		<textarea
			id={id}
			placeholder={placeholder}
			value={value}
			onChange={onChange}
			rows={rows}
			style={{
				width: "100%",
				padding: "10px 14px",
				fontSize: 14,
				fontFamily: "var(--font-body)",
				color: "var(--color-dashboard-text)",
				background: "var(--color-dashboard-paper)",
				border: "1px solid var(--color-dashboard-border)",
				borderRadius: 7,
				outline: "none",
				resize: "vertical",
				transition: "border-color 200ms, box-shadow 200ms",
				lineHeight: 1.5,
			}}
			onFocus={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-accent)";
				e.currentTarget.style.boxShadow =
					"0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)";
			}}
			onBlur={(e) => {
				e.currentTarget.style.borderColor = "var(--color-dashboard-border)";
				e.currentTarget.style.boxShadow = "none";
			}}
		/>
	);
}

export function AddSectionDialog({
	boqId,
	open,
	onOpenChange,
	onSuccess,
}: AddSectionDialogProps) {
	const [name, setName] = useState("");
	const [category, setCategory] = useState("");
	const [description, setDescription] = useState("");
	const [isSubmitting, setIsSubmitting] = useState(false);
	const firstInputRef = useRef<HTMLInputElement>(null);

	// Focus first input when opened
	useEffect(() => {
		if (open) {
			setTimeout(() => firstInputRef.current?.focus(), 80);
		}
	}, [open]);

	// Close on Escape
	useEffect(() => {
		if (!open) return;
		const handle = (e: KeyboardEvent) => {
			if (e.key === "Escape") onOpenChange(false);
		};
		window.addEventListener("keydown", handle);
		return () => window.removeEventListener("keydown", handle);
	}, [open, onOpenChange]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (!name.trim()) return;

		setIsSubmitting(true);
		try {
			const result = await addBOQSection(boqId, {
				sectionName: name.trim(),
				category: category.trim() || undefined,
				description: description.trim() || undefined,
			});

			if (result.success) {
				toast.success("Section added successfully");
				setName("");
				setCategory("");
				setDescription("");
				onSuccess();
				onOpenChange(false);
			} else {
				toast.error(result.error || "Failed to add section");
			}
		} finally {
			setIsSubmitting(false);
		}
	};

	if (!open) return null;

	return (
		<div
			role="dialog"
			aria-modal="true"
			style={{
				position: "fixed",
				inset: 0,
				zIndex: 200,
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				padding: "20px 16px",
				background: "rgba(30, 41, 59, 0.45)",
				backdropFilter: "blur(4px)",
			}}
			onClick={(e) => {
				if (e.target === e.currentTarget) onOpenChange(false);
			}}
			onKeyDown={(e) => {
				if (e.key === "Escape") onOpenChange(false);
			}}
		>
			<FloatPanel
				elevation={4}
				padding={0}
				style={{ width: "100%", maxWidth: 460, overflow: "hidden" }}
			>
				{/* Header */}
				<div
					style={{
						display: "flex",
						alignItems: "center",
						justifyContent: "space-between",
						padding: "20px 24px 16px",
						borderBottom: "1px solid var(--color-dashboard-border)",
					}}
				>
					<div>
						<p
							style={{
								fontSize: 10,
								fontFamily: "var(--font-mono)",
								fontWeight: 600,
								letterSpacing: "0.14em",
								textTransform: "uppercase",
								color: "var(--color-dashboard-accent-dark)",
								marginBottom: 4,
							}}
						>
							BOQ
						</p>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontStyle: "italic",
								fontSize: 20,
								fontWeight: 500,
								color: "var(--color-dashboard-text)",
								letterSpacing: "-0.01em",
							}}
						>
							Add Section
						</h2>
					</div>
					<button
						type="button"
						onClick={() => onOpenChange(false)}
						style={{
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
							width: 32,
							height: 32,
							borderRadius: 6,
							border: "1px solid var(--color-dashboard-border)",
							background: "transparent",
							cursor: "pointer",
							color: "var(--color-dashboard-muted)",
							transition: "background 150ms",
						}}
						onMouseEnter={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"var(--color-dashboard-divider)")
						}
						onMouseLeave={(e) =>
							((e.currentTarget as HTMLElement).style.background =
								"transparent")
						}
					>
						<X size={15} strokeWidth={2} />
					</button>
				</div>

				{/* Body */}
				<form onSubmit={handleSubmit}>
					<div
						style={{
							padding: "20px 24px",
							display: "flex",
							flexDirection: "column",
							gap: 16,
						}}
					>
						<div>
							<FieldInput
								label="Section Name *"
								id="section-name"
								placeholder="e.g. Electrical Works"
								value={name}
								onChange={(e) => setName(e.target.value)}
								required
							/>
						</div>
						<div>
							<FieldInput
								label="Category (optional)"
								id="section-category"
								placeholder="e.g. electrical, civil, plumbing"
								value={category}
								onChange={(e) => setCategory(e.target.value)}
							/>
						</div>
						<div>
							<FieldLabel htmlFor="section-desc">
								Description (optional)
							</FieldLabel>
							<FieldTextarea
								id="section-desc"
								placeholder="Provide context for this section…"
								value={description}
								onChange={(e) => setDescription(e.target.value)}
								rows={3}
							/>
						</div>
					</div>

					{/* Footer */}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							gap: 10,
							padding: "16px 24px",
							borderTop: "1px solid var(--color-dashboard-border)",
							background: "var(--color-dashboard-surface)",
						}}
					>
						<StudioButton
							variant="secondary"
							size="sm"
							type="button"
							onClick={() => onOpenChange(false)}
						>
							Cancel
						</StudioButton>
						<StudioButton
							variant="primary"
							size="sm"
							type="submit"
							disabled={isSubmitting || !name.trim()}
						>
							{isSubmitting ? "Adding…" : "Add Section"}
						</StudioButton>
					</div>
				</form>
			</FloatPanel>
		</div>
	);
}
